import React, { useEffect, useState, useMemo } from "react";
import { Dropdown, Spinner, Row, Col } from "react-bootstrap";
import AdvanceTable from "components/common/advance-table/AdvanceTable";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import CardDropdown from "components/common/CardDropdown";
import IconButton from "components/common/IconButton";
import TablePaginationFooter from "components/common/TablePaginationFooter";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import useAdvanceTable from "hooks/useAdvanceTable";
import { ConfirmDeleteModal } from "components/common/ConfirmDeleteModal";
import useAirports from "hooks/airports/useAirports";
import dayjs from "dayjs";
import { Drawer, Form, Input, Select, Button as AntButton, Typography } from "antd";
import {
  ExpandOutlined,
  EditOutlined,
  EnvironmentOutlined,
  NumberOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
} from "@ant-design/icons";

const { Title, Text } = Typography;

// --- Constants & Helpers ---

const categoryOptions = [
  { label: "🌍 ALL STATUS", value: "ALL" },
  { label: "✅ ACTIVE", value: "ACTIVE" },
  { label: "🚫 INACTIVE", value: "INACTIVE" },
];

const getAirportCode = (airport) =>
  airport?.iataCode || airport?.code || airport?.airportCode || "-";

const getLocation = (airport) => {
  const city = airport?.city || airport?.municipality || "";
  const country = airport?.country || airport?.countryName || "";
  return [city, country].filter(Boolean).join(", ") || "-";
};

// --- Sub-Components ---

const MetaRow = ({ icon, label, value, isLast = false }) => (
  <div style={{ display: "flex", alignItems: "flex-start", marginBottom: isLast ? 0 : 12 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#6b7280", fontSize: 13, flex: 1 }}>
      {icon}
      <span>{label}</span>
    </div>
    <div style={{ flex: 2 }}>{value}</div>
  </div>
);

const AirportDetailsDrawer = ({ visible, airport, loading, onClose, onEditAirport }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  useEffect(() => { if (!visible) setIsExpanded(false); }, [visible]);

  const isInactive = airport?.active === false;
  const statusStyle = isInactive
    ? { bg: "#FDECEC", text: "#DC2626", label: "Inactive" }
    : { bg: "#E6F9F0", text: "#059669", label: "Active" };

  return (
    <Drawer
      open={visible}
      onClose={onClose}
      placement="right"
      size={isExpanded ? "100%" : 520}
      zIndex={2000}
      styles={{ padding: 24 }}
      title={null}
      closable={false}
      destroyOnHidden
    >
      <div style={{ maxWidth: isExpanded ? 700 : "100%", margin: isExpanded ? "0 auto" : 0, fontFamily: "Inter, sans-serif" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
          <div style={{ fontSize: 13, color: "#6b7280" }}>
            <span style={{ cursor: "pointer" }}>Airports</span>
            <span style={{ margin: "0 6px" }}>/</span>
            <span style={{ color: "#111827", fontWeight: 500 }}>{airport?.name || "Airport Details"}</span>
          </div>
          <div style={{ display: "flex", gap: 16 }}>
            <ExpandOutlined onClick={() => setIsExpanded((prev) => !prev)} style={{ cursor: "pointer" }} />
            <EditOutlined onClick={() => airport?.id && onEditAirport?.(airport.id)} style={{ cursor: "pointer" }} />
          </div>
        </div>

        {loading && !airport ? (
          <div className="d-flex justify-content-center py-4"><Spinner animation="border" variant="success" /></div>
        ) : (
          <>
            <Title level={4} style={{ marginBottom: 8 }}>{airport?.name || "Airport Details"}</Title>
            <div className="d-flex flex-wrap gap-2 mb-3">
              <span className="d-inline-flex align-items-center gap-1 px-2 py-1 rounded-pill fw-medium" style={{ backgroundColor: "#EEF2FF", color: "#4F46E5", fontSize: "0.75rem" }}>
                <NumberOutlined /> {getAirportCode(airport)}
              </span>
              <span className="d-inline-flex align-items-center gap-1 px-2 py-1 rounded-pill fw-medium" style={{ backgroundColor: statusStyle.bg, color: statusStyle.text, fontSize: "0.75rem" }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: statusStyle.text }} />
                {statusStyle.label}
              </span>
            </div>
            <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, background: "#fafafa", padding: 14, marginBottom: 12 }}>
              <MetaRow icon={<EnvironmentOutlined />} label="City" value={<Text>{airport?.city || airport?.municipality || "—"}</Text>} />
              <MetaRow icon={<EnvironmentOutlined />} label="State" value={<Text>{airport?.state || "—"}</Text>} />
              <MetaRow icon={<EnvironmentOutlined />} label="Country" value={<Text>{airport?.country || airport?.countryName || "—"}</Text>} />
              <MetaRow icon={<EnvironmentOutlined />} label="Address" value={<Text>{airport?.address || "—"}</Text>} isLast />
            </div>
            <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, background: "#fff", padding: 14, marginBottom: 12 }}>
              <MetaRow icon={<ClockCircleOutlined />} label="Timezone" value={<Text>{airport?.timezone || "—"}</Text>} />
              <MetaRow icon={<NumberOutlined />} label="Coordinates" value={<Text>{airport?.latitude ?? "—"}, {airport?.longitude ?? "—"}</Text>} isLast />
            </div>
            <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, background: "#fff", padding: 14 }}>
              <MetaRow icon={<CalendarOutlined />} label="Created At" value={<Text>{airport?.createdAt ? dayjs(airport.createdAt).format("MMM D, YYYY HH:mm") : "—"}</Text>} />
              <MetaRow icon={<CalendarOutlined />} label="Updated At" value={<Text>{airport?.updatedAt ? dayjs(airport.updatedAt).format("MMM D, YYYY HH:mm") : "—"}</Text>} isLast />
            </div>
          </>
        )}
      </div>
    </Drawer>
  );
};

const AirportFormDrawer = ({ show, onClose, mode = "create", initialValue, loading, onSubmit }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [countryOptions, setCountryOptions] = useState([]);
  const [isLoadingCountries, setIsLoadingCountries] = useState(false);
  const [form] = Form.useForm();
  const isEditMode = mode === "edit";

  useEffect(() => {
    if (!show) return;
    form.setFieldsValue({
      name: initialValue?.name || "",
      code: getAirportCode(initialValue) !== "-" ? getAirportCode(initialValue) : "",
      city: initialValue?.city || initialValue?.municipality || "",
      state: initialValue?.state || "",
      country: initialValue?.country || initialValue?.countryName || undefined,
      active: initialValue?.active === false ? "inactive" : "active",
    });
  }, [form, initialValue, show]);

  useEffect(() => {
    let mounted = true;
    const fetchCountries = async () => {
      setIsLoadingCountries(true);
      try {
        const response = await fetch("https://restcountries.com/v3.1/all?fields=name,cca2");
        const payload = await response.json();
        if (mounted && Array.isArray(payload)) {
          setCountryOptions(payload.map(i => ({ label: `${i.name.common} (${i.cca2})`, value: i.cca2 })).sort((a,b) => a.label.localeCompare(b.label)));
        }
      } catch (e) { console.error(e); } finally { if (mounted) setIsLoadingCountries(false); }
    };
    if (show) fetchCountries();
    return () => { mounted = false; };
  }, [show]);

  const handleSave = async (values) => {
    setIsSubmitting(true);
    try {
      const payload = {
        code: values.code.trim().toUpperCase(),
        name: values.name.trim(),
        city: values.city?.trim() || "",
        country: values.country || "",
        active: values.active !== "inactive",
      };
      await onSubmit(payload);
      form.resetFields();
      onClose();
    } finally { setIsSubmitting(false); }
  };

  return (
    <Drawer
      zIndex={2000} placement="right" closable={false} size={isExpanded ? "large" : 560}
      onClose={onClose} open={show} destroyOnHidden
      footer={
        <div style={{ textAlign: "right", padding: "10px 0" }}>
          <AntButton style={{ marginRight: 8 }} onClick={onClose} disabled={isSubmitting}>Cancel</AntButton>
          <AntButton type="primary" onClick={() => form.submit()} loading={isSubmitting || loading}>
            {isEditMode ? "Save Changes" : "Create Airport"}
          </AntButton>
        </div>
      }
    >
      <div className="d-flex justify-content-between mb-3">
        <Text style={{ fontSize: 15, color: "#6b7280" }}>
          Airport / <span style={{ color: "#111827", fontWeight: 500 }}>{isEditMode ? "Edit" : "Create"}</span>
        </Text>
        <ExpandOutlined onClick={() => setIsExpanded(!isExpanded)} style={{ cursor: "pointer" }} />
      </div>
      <Form layout="vertical" form={form} onFinish={handleSave}>
        <Form.Item label="Airport Name" name="name" rules={[{ required: true }]}><Input /></Form.Item>
        <Form.Item label="Code" name="code" rules={[{ required: true }]}><Input maxLength={8} /></Form.Item>
        <Form.Item label="City" name="city"><Input /></Form.Item>
        <Form.Item label="Country" name="country">
          <Select showSearch options={countryOptions} loading={isLoadingCountries} />
        </Form.Item>
        <Form.Item label="Status" name="active">
          <Select options={[{ label: "Active", value: "active" }, { label: "Inactive", value: "inactive" }]} />
        </Form.Item>
      </Form>
    </Drawer>
  );
};

// --- Main Component ---

export const Airports = () => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  
  // FIX: Added missing state variables for the Search and Select components
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("ALL");

  const {
    airports = [],
    selectedAirport,
    loading,
    totalPages,
    totalElements,
    fetchAirportById,
    createAirport,
    updateAirport,
    deleteAirport,
  } = useAirports(page, pageSize);

  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showFormDrawer, setShowFormDrawer] = useState(false);
  const [formMode, setFormMode] = useState("create");
  const [formAirport, setFormAirport] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [airportToDelete, setAirportToDelete] = useState(null);

  const handleOpenEdit = async (id) => {
    const airport = await fetchAirportById(id);
    setFormMode("edit");
    setFormAirport(airport);
    setShowFormDrawer(true);
  };

  // Logic to filter the table data based on the search bar
  const filteredAirports = useMemo(() => {
    return airports.filter((item) => {
      const name = item.name?.toLowerCase() || "";
      const code = getAirportCode(item).toLowerCase();
      const location = getLocation(item).toLowerCase();
      const search = searchTerm.toLowerCase();

      const matchesSearch = name.includes(search) || code.includes(search) || location.includes(search);
      
      const matchesStatus = 
        selectedCategory === "ALL" || 
        (selectedCategory === "ACTIVE" ? item.active !== false : item.active === false);

      return matchesSearch && matchesStatus;
    });
  }, [airports, searchTerm, selectedCategory]);

  const columns = useMemo(() => [
    {
      accessorKey: "name",
      header: "Airport Name",
      meta: { headerProps: { className: "text-900 fw-bold" } },
    },
    {
      id: "code",
      header: "Code",
      accessorFn: (row) => getAirportCode(row),
      cell: ({ getValue }) => <span className="font-monospace fw-bold text-primary">{getValue()}</span>,
      meta: { headerProps: { className: "text-900 fw-bold" } },
    },
    {
      id: "location",
      header: "Location",
      accessorFn: (row) => getLocation(row),
      meta: { headerProps: { className: "text-900 fw-bold" } },
    },
    {
      id: "status",
      header: "Status",
      meta: { 
        headerProps: { className: "text-center text-900 fw-bold", style: { width: '100px' } },
        cellProps: { className: "text-center" } 
      },
      cell: ({ row }) => {
        const isActive = row.original.active !== false;
        const style = {
          backgroundColor: isActive ? "#e8f7e3" : "#fbe9e7",
          color: isActive ? "#2e7d32" : "#c62828",
          padding: "2px 8px",
          borderRadius: "4px",
          fontSize: "0.7rem",
          fontWeight: 600,
          border: isActive ? "1px solid #d5ebd0" : "1px solid #f5c6cb",
          display: "inline-block",
          textTransform: "uppercase"
        };
        return <span style={style}>{isActive ? "ACTIVE" : "INACTIVE"}</span>;
      },
    },
    {
      id: "actions",
      header: "Actions",
      meta: { 
        headerProps: { className: "text-center text-900 fw-bold", style: { width: '80px' } },
        cellProps: { className: "text-center" } 
      },
      cell: ({ row }) => (
        <CardDropdown drop="start">
          <div className="py-2 fw-normal">
            <Dropdown.Item onClick={() => { fetchAirportById(row.original.id); setShowDetailsModal(true); }}>View</Dropdown.Item>
            <Dropdown.Item onClick={() => handleOpenEdit(row.original.id)}>Edit</Dropdown.Item>
            <Dropdown.Divider />
            <Dropdown.Item className="text-danger" onClick={() => { setAirportToDelete(row.original.id); setShowDeleteModal(true); }}>Delete</Dropdown.Item>
          </div>
        </CardDropdown>
      ),
    },
  ], []);

  const table = useAdvanceTable({ data: filteredAirports, columns, sortable: true, pagination: true, perPage: pageSize });

  return (
    <>
      <WidgetSectionTitle icon="plane-departure" title="Airports" subtitle="Manage airport records." className="mb-4 mt-3" />

      <div className="d-flex justify-content-end mb-4">
        <IconButton
          variant="falcon-default" size="sm" icon="plus"
          onClick={() => { setFormMode("create"); setFormAirport(null); setShowFormDrawer(true); }}
          style={{ backgroundColor: "#10b981", color: "#fff", borderColor: "#10b981", fontWeight: 600 }}
        >
          Add New Airport
        </IconButton>
      </div>

     {/* 1. Use the specific Row layout you provided */}
<Row className="mb-2 g-3 align-items-center">
  <Col md={8} lg={9}>
    <div className="d-flex align-items-center gap-3 flex-wrap">
      <Input
        prefix={<span style={{ fontSize: 14 }}>🔍</span>}
        placeholder="Search by name, code or location..." // Placeholder adapted for Airports
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        allowClear
        size="large"
        style={{ borderRadius: 12, height: 30, flex: "1 1 300px", maxWidth: 300 }}
      />
      <Select
        size="large"
        value={selectedCategory}
        onChange={setSelectedCategory}
        options={categoryOptions}
        style={{ minWidth: 180, height: 28 }}
      />
    </div>
  </Col>
  <Col md={4} lg={3} className="text-end">
    <div style={{ fontSize: 13, color: "#64748b" }}>
      {/* 2. Swapped 'affiliations' for your actual data variables */}
      Results: <span className="fw-semibold text-dark">{filteredAirports.length}</span> of <span className="fw-semibold text-dark">{totalElements || 0}</span>
    </div>
  </Col>
</Row>

      <AdvanceTableProvider {...table}>
        {loading && airports.length === 0 ? (
          <div className="text-center py-5"><Spinner animation="border" variant="success" size="sm" /></div>
        ) : (
          <>
            <AdvanceTable 
              headerClassName="bg-light text-nowrap align-middle text-dark" 
              rowClassName="align-middle white-space-nowrap"
              tableProps={{ bordered: true, striped: true, className: "fs-10 mb-0 overflow-hidden" }} 
            />
            <div className="mt-3">
              <TablePaginationFooter
                page={page} totalPages={totalPages} totalElements={totalElements}
                onPrev={() => setPage(p => Math.max(p - 1, 0))}
                onNext={() => setPage(p => Math.min(p + 1, totalPages - 1))}
                pageSize={pageSize} onPageSizeChange={(s) => { setPageSize(s); setPage(0); }}
                pageSizeOptions={[10, 20, 50, 100]}
              />
            </div>
          </>
        )}
      </AdvanceTableProvider>

      <AirportDetailsDrawer
        visible={showDetailsModal} onClose={() => setShowDetailsModal(false)}
        airport={selectedAirport} loading={loading}
        onEditAirport={(id) => { setShowDetailsModal(false); handleOpenEdit(id); }}
      />
      
      <AirportFormDrawer
        show={showFormDrawer} onClose={() => setShowFormDrawer(false)}
        mode={formMode} initialValue={formAirport} loading={loading}
        onSubmit={async (val) => {
          if (formMode === "edit") await updateAirport(formAirport.id, val);
          else await createAirport(val);
        }}
      />
      
      <ConfirmDeleteModal
        show={showDeleteModal} setShow={setShowDeleteModal}
        onConfirm={() => deleteAirport(airportToDelete)}
        itemName="airport" loading={loading}
      />
    </>
  );
};